import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [offices, setOffices] = useState([]);
  const [currentOffice, setCurrentOffice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinResetRequired, setPinResetRequired] = useState(false);
  const [activeView, setActiveView] = useState(null); // 'admin' | 'employee' for office admins
  const [sessionConfig, setSessionConfig] = useState({
    inactivity_timeout_minutes: 30,
    warning_before_logout_minutes: 2
  });
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  
  // Refs for timers
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const sessionValidationRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch session config on mount
  useEffect(() => {
    const fetchSessionConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/session-config`);
        if (response.ok) {
          const config = await response.json();
          setSessionConfig(config);
        }
      } catch (error) {
        console.log('Using default session config');
      }
    };
    fetchSessionConfig();
  }, [API_URL]);

  // Load auth data from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');
    const savedOffices = localStorage.getItem('auth_offices');
    const savedCurrentOffice = localStorage.getItem('current_office');
    const savedActiveView = localStorage.getItem('active_view');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      
      if (savedOffices) {
        setOffices(JSON.parse(savedOffices));
      }
      
      if (savedCurrentOffice) {
        setCurrentOffice(JSON.parse(savedCurrentOffice));
      }
      
      // Restore active view for office admins
      if (savedActiveView) {
        setActiveView(savedActiveView);
      }
    }
    
    setLoading(false);
  }, []);

  // Session terminated handler
  const handleSessionTerminated = useCallback((reason = 'session_expired') => {
    // Clear timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (sessionValidationRef.current) clearInterval(sessionValidationRef.current);
    
    // Clear state
    setUser(null);
    setToken(null);
    setOffices([]);
    setCurrentOffice(null);
    setPinResetRequired(false);
    setShowSessionWarning(false);
    
    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_offices');
    localStorage.removeItem('current_office');
    
    // Show appropriate message
    const messages = {
      'session_expired': 'Your session has expired. Please login again.',
      'inactivity': 'You have been logged out due to inactivity.',
      'force_logout': 'You have been logged out from another device.',
      'SESSION_TERMINATED': 'Your session was terminated. You may have logged in from another device.',
      'SESSION_NOT_FOUND': 'Session not found. Please login again.'
    };
    
    toast.error(messages[reason] || 'Session ended. Please login again.', {
      duration: 5000,
    });
  }, []);

  // Validate session with backend
  const validateSession = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/validate-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        handleSessionTerminated(data.detail || 'session_expired');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return true; // Don't logout on network errors
    }
  }, [token, API_URL, handleSessionTerminated]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowSessionWarning(false);
    
    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    if (!token) return;
    
    const warningTime = (sessionConfig.inactivity_timeout_minutes - sessionConfig.warning_before_logout_minutes) * 60 * 1000;
    const logoutTime = sessionConfig.inactivity_timeout_minutes * 60 * 1000;
    
    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowSessionWarning(true);
      toast.warning('Your session will expire soon due to inactivity. Move your mouse or press any key to stay logged in.', {
        duration: sessionConfig.warning_before_logout_minutes * 60 * 1000,
        id: 'session-warning'
      });
    }, warningTime);
    
    // Set logout timer
    inactivityTimerRef.current = setTimeout(() => {
      handleSessionTerminated('inactivity');
    }, logoutTime);
  }, [token, sessionConfig, handleSessionTerminated]);

  // Activity event listeners
  useEffect(() => {
    if (!token) return;
    
    const handleActivity = () => {
      resetInactivityTimer();
    };
    
    // Listen for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Start inactivity timer
    resetInactivityTimer();
    
    // Validate session periodically (every 2 minutes)
    sessionValidationRef.current = setInterval(() => {
      validateSession();
    }, 2 * 60 * 1000);
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (sessionValidationRef.current) clearInterval(sessionValidationRef.current);
    };
  }, [token, resetInactivityTimer, validateSession]);

  const login = async (phone, pin) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, pin }),
      });

      // Clone response before reading to avoid "body already used" errors
      const resClone = res.clone();
      
      let data;
      try {
        data = await res.json();
      } catch (parseError) {
        // If parsing fails, try the clone
        try {
          data = await resClone.json();
        } catch {
          // If both fail, create error based on status
          if (res.status === 429) {
            return { success: false, error: 'Account locked. Too many failed attempts. Try again in 15 minutes.' };
          }
          return { success: false, error: 'Connection error. Please try again.' };
        }
      }
      
      if (!res.ok) {
        return { success: false, error: data.detail || 'Login failed' };
      }
      
      // Store auth data
      setToken(data.access_token);
      setUser(data.user);
      setOffices(data.offices || []);
      setPinResetRequired(data.pin_reset_required || false);
      
      // Auto-select first office if available and user is not super admin
      if (data.offices && data.offices.length > 0 && !data.user.roles.some(role => role.role === 'super_admin')) {
        setCurrentOffice(data.offices[0]);
        localStorage.setItem('current_office', JSON.stringify(data.offices[0]));
      }
      
      // Set default active view for office admins
      const isAdmin = data.user.roles.some(role => role.role === 'office_admin');
      if (isAdmin) {
        setActiveView('admin');
        localStorage.setItem('active_view', 'admin');
      }
      
      // Save to localStorage
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      localStorage.setItem('auth_offices', JSON.stringify(data.offices || []));
      
      // Reset activity timer
      lastActivityRef.current = Date.now();
      
      return { success: true, user: data.user, offices: data.offices, pinResetRequired: data.pin_reset_required };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Connection error. Please try again.' };
    }
  };

  const logout = async () => {
    // Call backend logout endpoint
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    // Clear timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (sessionValidationRef.current) clearInterval(sessionValidationRef.current);
    
    setUser(null);
    setToken(null);
    setOffices([]);
    setCurrentOffice(null);
    setPinResetRequired(false);
    setShowSessionWarning(false);
    setActiveView(null);
    
    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_offices');
    localStorage.removeItem('current_office');
    localStorage.removeItem('active_view');
  };

  const logoutAllSessions = async () => {
    if (!token) return { success: false };
    
    try {
      const response = await fetch(`${API_URL}/api/auth/logout-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        return { success: true, count: data.sessions_terminated };
      }
      return { success: false };
    } catch (error) {
      console.error('Logout all error:', error);
      return { success: false };
    }
  };

  const getActiveSessions = async () => {
    if (!token) return [];
    
    try {
      const response = await fetch(`${API_URL}/api/auth/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.sessions || [];
      }
      return [];
    } catch (error) {
      console.error('Get sessions error:', error);
      return [];
    }
  };

  const terminateSession = async (sessionId) => {
    if (!token) return { success: false };
    
    try {
      const response = await fetch(`${API_URL}/api/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Session terminated');
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error('Terminate session error:', error);
      return { success: false };
    }
  };

  const extendSession = () => {
    // Reset timers when user explicitly wants to stay logged in
    resetInactivityTimer();
    toast.dismiss('session-warning');
    toast.success('Session extended');
  };

  const switchOffice = (office) => {
    setCurrentOffice(office);
    localStorage.setItem('current_office', JSON.stringify(office));
  };

  // Switch between admin and employee view for office admins
  const switchView = (view) => {
    if (isOfficeAdmin()) {
      setActiveView(view);
      localStorage.setItem('active_view', view);
    }
  };

  // Check if user can switch views (office admins only)
  const canSwitchView = () => {
    return user && user.roles && user.roles.some(role => role.role === 'office_admin');
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isSuperAdmin = () => {
    return user && user.roles && user.roles.some(role => role.role === 'super_admin');
  };

  const isOfficeAdmin = () => {
    return user && user.roles && user.roles.some(role => role.role === 'office_admin');
  };

  // Returns true if viewing as employee (either actual employee or admin in employee view)
  const isEmployee = () => {
    if (!user || !user.roles) return false;
    
    // Office admin viewing as employee
    if (isOfficeAdmin() && activeView === 'employee') {
      return true;
    }
    
    // Regular employee (not office admin)
    return user.roles.some(role => role.role === 'employee') && !isOfficeAdmin();
  };

  // Returns true if viewing as admin (actual admin view)
  const isViewingAsAdmin = () => {
    return isOfficeAdmin() && activeView !== 'employee';
  };

  const hasRole = (roleName, officeId = null) => {
    if (!user || !user.roles) return false;
    
    // Super admin has all roles
    if (isSuperAdmin()) return true;
    
    return user.roles.some(role => {
      if (officeId) {
        return role.role === roleName && role.office_id === officeId;
      }
      return role.role === roleName;
    });
  };

  const getUserRole = () => {
    if (!user || !user.roles || user.roles.length === 0) return null;
    
    // Return the primary role
    const role = user.roles[0];
    if (role.role === 'super_admin') return 'Super Admin';
    if (role.role === 'office_admin') return 'Office Admin';
    if (role.role === 'employee') return role.job_title || 'Employee';
    return role.role;
  };

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    if (currentOffice && !isSuperAdmin()) {
      headers['X-Office-ID'] = currentOffice.id;
    }
    
    return headers;
  };

  const changePin = async (currentPin, newPin) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/change-pin`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ current_pin: currentPin, new_pin: newPin }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'PIN change failed');
      }

      setPinResetRequired(false);
      return { success: true };
    } catch (error) {
      console.error('PIN change error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    token,
    offices,
    currentOffice,
    loading,
    pinResetRequired,
    sessionConfig,
    showSessionWarning,
    activeView,
    login,
    logout,
    logoutAllSessions,
    getActiveSessions,
    terminateSession,
    extendSession,
    switchOffice,
    switchView,
    canSwitchView,
    isViewingAsAdmin,
    isAuthenticated,
    isSuperAdmin,
    isOfficeAdmin,
    isEmployee,
    hasRole,
    getUserRole,
    getAuthHeaders,
    changePin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
